import { Component, inject, Inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { forkJoin, Observable } from 'rxjs';
import Swal from 'sweetalert2';
import { PacienteService } from '../../../core/services/pacientes.service';
import { UsuarioService } from '../../../core/services/usuario.service';
import { RolesService } from '../../../core/services/roles.service';
import { ActualizarCedulaRequest, CedulaService } from '../../../core/services/cedula.service';
import {
  cedulasSinDuplicados,
  PATRON_CEDULA,
  TIPOS_CEDULA,
} from '../../../core/validators/cedula.validators';

@Component({
  selector: 'app-alta-personal-dialog',
  imports: [
    MatStepperModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogContent,
    MatOption,
    MatSelectModule,
    MatDialogModule,
  ],
  templateUrl: './alta-personal-dialog.html',
  styleUrl: './alta-personal-dialog.scss',
})
export class AltaPersonalDialog {
  constructor(
    private dialogRef: MatDialogRef<AltaPersonalDialog>,
    private usuarioService: UsuarioService,
    private rolesService: RolesService,
    private cedulaProfesionalService: CedulaService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  private fb = inject(FormBuilder);
  mostrarPassword = false;
  roles: any[] = [];

  get esEdicion(): boolean {
    return !!this.data?.personal;
  }

  tiposCedula = TIPOS_CEDULA;

  form = this.fb.group({
    nombre: ['', Validators.required],
    apellidoPaterno: ['', Validators.required],
    apellidoMaterno: [''],
    rolId: [null as number | null, Validators.required],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],

    cedulas: this.fb.array([this.crearFormularioCedula()], cedulasSinDuplicados),
  });

  private crearFormularioCedula() {
    return this.fb.group({
      id: [null as string | number | null],
      tipoCedula: ['Licenciatura', Validators.required],
      cedula: ['', [Validators.required, Validators.pattern(PATRON_CEDULA)]],
    });
  }

  puedeQuitarCedula(index: number): boolean {
    const tieneId = this.cedulas.at(index).get('id')?.value != null;
    return this.cedulas.length > 1 && (!this.esEdicion || !tieneId);
  }

  get cedulas(): FormArray {
    return this.form.controls.cedulas;
  }

  agregarCedula(): void {
    this.cedulas.push(this.crearFormularioCedula());
  }

  eliminarCedula(index: number): void {
    // Debe conservarse al menos una cédula.
    if (this.cedulas.length <= 1) {
      return;
    }

    this.cedulas.removeAt(index);
  }

  passwordForm = this.fb.group({
    password: ['', [Validators.required]],
    passwordNew: ['', [Validators.required, Validators.minLength(8)]],
    passwordConfirm: ['', [Validators.required, Validators.minLength(8)]],
  });

  mostrarPasswordActual = false;
  mostrarPasswordNueva = false;
  mostrarPasswordConfirmar = false;

  esPerfilPropio = false;

  private cedulasOriginales = new Map<string | number, { tipoCedula: string; cedula: string }>();

  ngOnInit(): void {
    this.esPerfilPropio = this.data?.perfilPropio === true;

    this.cargarRoles();

    if (this.esEdicion) {
      const personal = this.data.personal;

      // console.log('Personal que se va a editar:', personal);
      // console.log('Cédulas del personal:', personal.cedulas);

      this.form.patchValue({
        nombre: personal.nombre ?? '',
        apellidoPaterno: personal.apellidoPaterno ?? '',
        apellidoMaterno: personal.apellidoMaterno ?? '',
        rolId: personal.rolId ?? personal.rol?.id ?? null,
      });

      this.form.get('email')?.disable();
      this.form.get('password')?.disable();

      this.form.get('email')?.clearValidators();
      this.form.get('password')?.clearValidators();

      this.form.get('email')?.updateValueAndValidity();
      this.form.get('password')?.updateValueAndValidity();

      this.cedulas.clear();

      const cedulasExistentes = personal.cedulas ?? [];

      if (cedulasExistentes.length > 0) {
        cedulasExistentes.forEach((cedula: any) => {
          const cedulaTexto = String(cedula.cedula ?? '');
          const tipo = cedula.tipoCedula ?? 'Licenciatura';

          this.cedulasOriginales.set(cedula.id, { tipoCedula: tipo, cedula: cedulaTexto });

          this.cedulas.push(
            this.fb.group({
              id: [cedula.id],
              tipoCedula: [tipo, Validators.required],
              cedula: [cedulaTexto, [Validators.required, Validators.pattern(PATRON_CEDULA)]],
            }),
          );
        });
      } else {
        // Permite regularizar visualmente al personal antiguo.
        this.cedulas.push(this.crearFormularioCedula());
      }

      // console.log('Cédulas cargadas en el formulario:', this.cedulas.getRawValue());
    }
  }

  cargarRoles(): void {
    this.rolesService.obtenerRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
      },
      error: (error) => {
        // console.error('Error al cargar roles:', error);

        Swal.fire({
          icon: 'error',
          title: 'Error al cargar roles',
          text: error?.error?.message ?? 'No se pudieron cargar los roles.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  cerrar() {
    this.dialogRef.close();
  }

  guardar() {
    // console.log('Formulario válido:', this.form.valid);
    // console.log('Errores del formulario:', this.form.errors);
    // console.log('Cédulas antes de guardar:', this.cedulas.getRawValue());
    // console.log('Valores completos:', this.form.getRawValue());

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.esEdicion) {
      this.guardarEdicion();
    } else {
      this.guardarNuevo();
    }
  }

  // ==========================================
  // CREAR NUEVO (POST /users)
  // ==========================================

  private guardarNuevo(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const valores = this.form.getRawValue();

    // console.log('Datos completos del formulario:', valores);
    // console.log('Cédulas que se van a guardar:', valores.cedulas);

    const cedulas = (valores.cedulas ?? []).map((item) => ({
      tipoCedula: item.tipoCedula!,
      cedula: Number(item.cedula),
    }));

    if (
      cedulas.length === 0 ||
      cedulas.some((item) => !Number.isSafeInteger(item.cedula) || item.cedula <= 0)
    ) {
      Swal.fire({
        icon: 'warning',
        title: 'Cédula obligatoria',
        text: 'Registra al menos una cédula profesional válida.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const payload = {
      nombre: valores.nombre?.trim(),
      apellidoPaterno: valores.apellidoPaterno?.trim(),
      apellidoMaterno: valores.apellidoMaterno?.trim(),
      email: valores.email,
      password: valores.password,
      rolId: valores.rolId,
      cedulas,
    };

    this.usuarioService.crearUsuario(payload).subscribe({
      next: (respuesta) => {
        Swal.fire({
          icon: 'success',
          title: 'Registro exitoso',
          text: 'El personal se registró con sus cédulas profesionales.',
          showConfirmButton: false,
          timer: 2000,
        }).then(() => {
          this.dialogRef.close(respuesta?.data ?? respuesta);
        });
      },
      error: (error) => {
        // console.error('Error al registrar personal:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo registrar',
          text: error?.error?.message ?? 'Ocurrió un error al registrar al personal.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  // ==========================================
  // EDITAR EXISTENTE (PATCH /users/:id + PATCH /users/:id/role)
  // ==========================================

  private guardarEdicion(): void {
    const personal = this.data.personal;
    const userId = personal?.id;

    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Usuario no identificado',
        text: 'No fue posible identificar al usuario que deseas editar.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const valores = this.form.getRawValue();

    const cedulas = (valores.cedulas ?? []).map((item) => ({
      id: item.id,
      tipoCedula: item.tipoCedula,
      cedula: (item.cedula ?? '').trim(),
    }));

    if (
      cedulas.length === 0 ||
      cedulas.some(
        (item) =>
          !item.tipoCedula ||
          !/^[0-9]+$/.test(item.cedula) ||
          !Number.isSafeInteger(Number(item.cedula)) ||
          Number(item.cedula) <= 0,
      )
    ) {
      Swal.fire({
        icon: 'warning',
        title: 'Cédulas inválidas',
        text: 'Registra al menos una cédula profesional válida y verifica que todas tengan tipo y número.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const datosNombre = {
      nombre: valores.nombre?.trim() ?? '',
      apellidoPaterno: valores.apellidoPaterno?.trim() ?? '',
      apellidoMaterno: valores.apellidoMaterno?.trim() ?? '',
    };

    const datosRol = { rolId: valores.rolId };

    Swal.fire({
      icon: 'question',
      title: '¿Guardar cambios?',
      text: 'Se actualizarán los datos personales, el rol y las cédulas profesionales.',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      const peticiones: Observable<any>[] = [
        this.usuarioService.actualizarUsuario(userId, datosNombre),
        this.usuarioService.actualizarUsuarioRol(datosRol, userId),
      ];

      for (const item of cedulas) {
        if (item.id != null) {
          // Cédula existente: PATCH solo con lo que cambió
          const original = this.cedulasOriginales.get(item.id);
          const cambios: ActualizarCedulaRequest = {};

          if (original?.cedula !== item.cedula) {
            cambios.cedula = Number(item.cedula);
          }
          if (original?.tipoCedula !== item.tipoCedula) {
            cambios.tipoCedula = item.tipoCedula!;
          }

          if (Object.keys(cambios).length > 0) {
            peticiones.push(this.cedulaProfesionalService.actualizarCedula(item.id, cambios));
          }
        } else {
          // Cédula nueva agregada durante la edición
          peticiones.push(
            this.cedulaProfesionalService.crearCedula({
              tipoCedula: item.tipoCedula!,
              cedula: Number(item.cedula),
              // usuarioId: userId, // descomenta/ajusta según tu backend
            }),
          );
        }
      }

      forkJoin(peticiones).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Cambios guardados',
            text: 'Los datos personales, el rol y las cédulas se actualizaron.',
            showConfirmButton: false,
            timer: 2000,
          }).then(() => {
            this.dialogRef.close(true);
          });
        },
        error: (error) => {
          // console.error('Error al actualizar el usuario:', error);
          // console.error('Respuesta del backend:', error?.error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo actualizar',
            text: error?.error?.message ?? 'Ocurrió un error al actualizar los datos del usuario.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  cambiarPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const valores = this.passwordForm.getRawValue();

    if (valores.passwordNew !== valores.passwordConfirm) {
      Swal.fire({
        icon: 'warning',
        title: 'Las contraseñas no coinciden',
        text: 'Verifica que la nueva contraseña y su confirmación sean iguales.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const payload = {
      password: valores.password,
      passwordNew: valores.passwordNew,
    };

    this.usuarioService.actualizarUsuarioPassword(payload).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'Contraseña actualizada',
          text: 'Tu contraseña se cambió correctamente.',
          showConfirmButton: false,
          timer: 2000,
        }).then(() => {
          this.passwordForm.reset();
        });
      },
      error: (error) => {
        // console.error('Error al cambiar la contraseña:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo cambiar la contraseña',
          text: error?.error?.message ?? 'La contraseña actual es incorrecta o ocurrió un error.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }
}
